import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Eye, EyeOff, TrendingDown, TrendingUp } from "lucide-react";
import { useApp } from "@/App";
import { useTranslation } from "react-i18next";
import { portfolioChange24h } from "@/lib/portfolio";

// Баланс — то, ради чего приложение открывают. Он и есть герой экрана, поэтому
// под ним своё свечение (см. .balance-section в index.css): число не подписано
// на сцене, а само её освещает.
export const BalanceSection = () => {
  const { t } = useTranslation();
  const {
    totalBalance,
    balances,
    market,
    hideBalance,
    setHideBalance,
    haptic,
    config,
    preferredCurrency,
    setPreferredCurrency
  } = useApp();

  // Сноска о курсе: открывается по «i», закрывается любым следующим тапом.
  //
  // Живёт в портале на body и позиционируется fixed по кнопке. Абсолютной её
  // держать нельзя: секции главной висят на .lp-rise, а это анимация с
  // transform и fill-mode both — после неё transform остаётся на элементе и
  // создаёт контекст наложения. Внутри него любой z-index сноски меряется
  // только с соседями ПО СЕКЦИИ, а «Быстрые действия» и список активов идут в
  // разметке следом и своими контекстами перекрывают её целиком. Именно так
  // сноска и уходила под кнопки: z-30 у неё был, толку — нет.
  const [showRateInfo, setShowRateInfo] = useState(false);
  // z-75 у сноски: выше нижней навигации (.bottom-nav, z-70) и всего, что
  // рисует главный экран, но ниже листов и диалогов (80-98) и замка
  // кода-пароля с тостами (100) — сноска не должна всплывать над ними.
  const [rateInfoPos, setRateInfoPos] = useState(null);
  const rateInfoBtnRef = useRef(null);

  const placeRateInfo = () => {
    const rect = rateInfoBtnRef.current?.getBoundingClientRect?.();
    if (!rect) return null;
    // Ширина сноски 16rem; на узком экране прижимаем её к краям с отступом 12px,
    // иначе центрирование по кнопке вынесло бы половину за пределы экрана.
    const width = 256;
    const half = width / 2;
    const margin = 12;
    const center = Math.min(
      Math.max(rect.left + rect.width / 2, half + margin),
      Math.max(window.innerWidth - half - margin, half + margin)
    );
    return { top: rect.bottom + 8, left: center };
  };

  useEffect(() => {
    if (!showRateInfo) return undefined;
    const close = () => setShowRateInfo(false);
    // Скролл и поворот экрана уводят кнопку из-под сноски: гнаться за ней
    // незачем, проще закрыть.
    const timer = setTimeout(() => {
      document.addEventListener("click", close);
      document.addEventListener("touchstart", close);
      window.addEventListener("scroll", close, true);
      window.addEventListener("resize", close);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("click", close);
      document.removeEventListener("touchstart", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [showRateInfo]);

  const toggleHide = () => {
    haptic('light');
    setHideBalance(!hideBalance);
  };

  const toggleCurrency = () => {
    haptic('light');
    setPreferredCurrency(preferredCurrency === 'USD' ? 'RUB' : 'USD');
  };

  const getRate = (currency) => {
    if (!currency || currency === 'USD') return 1;
    if (!config) return 1;
    const key = `usd_${currency.toLowerCase()}_rate`;
    return config[key] || 1;
  };

  const currencySymbols = {
    USD: '$',
    RUB: '₽',
  };

  const rate = getRate(preferredCurrency);
  const convertedBalance = totalBalance * rate;
  const symbol = currencySymbols[preferredCurrency] || '$';

  const formatBalance = (amount) => {
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    return formatted;
  };

  const displayAmount = hideBalance ? "•••••" : formatBalance(convertedBalance);

  // Изменение за сутки. Считается из тех же чисел, что показаны в списке
  // активов ниже, — иначе герой экрана расходился бы с содержимым экрана.
  const change = portfolioChange24h(balances, market);
  const rising = change.absolute >= 0;
  const TrendGlyph = rising ? TrendingUp : TrendingDown;
  const changeAbs = Math.abs(change.absolute) * rate;
  const changeText = hideBalance
    ? "••••"
    : `${rising ? "+" : "−"}${symbol}${changeAbs.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
  const percentText = `${rising ? "+" : "−"}${Math.abs(change.percent).toFixed(2)}%`;

  return (
    <div className="balance-section" data-testid="balance-section">
      <p className="balance-eyebrow">
        {preferredCurrency === 'USD'
          ? t('home.total_balance_usd') || "Общий баланс в USD"
          : `${t('home.total_balance') || "Общий баланс"} ${preferredCurrency}`}
      </p>

      <div className="balance-amount-wrapper relative">
        <div className="balance-amount flex items-center justify-center gap-1">
          {!hideBalance && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleCurrency();
              }}
              className="balance-dollar-sign !mt-0 !mr-0 leading-none active:scale-90 transition-transform"
              aria-label={t('home.toggle_currency', 'Переключить валюту')}
              data-testid="currency-toggle-btn"
            >
              {symbol}
            </button>
          )}
          <span className={`balance-amount-number ${hideBalance ? 'balance-hidden-text' : ''}`}>
            {displayAmount}
          </span>
          <button
            ref={rateInfoBtnRef}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              haptic('light');
              setShowRateInfo((v) => {
                if (v) return false;
                setRateInfoPos(placeRateInfo());
                return true;
              });
            }}
            className="ml-0.5 self-start mt-1 flex h-4 w-4 items-center justify-center rounded-full border border-muted-foreground/40 text-[9px] font-serif italic text-muted-foreground/70 opacity-60 transition-opacity hover:opacity-100"
            aria-label={t('home.rate_info_aria', 'О курсе')}
            data-testid="rate-info-btn"
          >
            i
          </button>
        </div>

        {showRateInfo && rateInfoPos && createPortal(
          <div
            className="lp-veil lp-veil--edged fixed z-[75] w-64 -translate-x-1/2 rounded-[16px] px-3 py-2.5 text-center text-xs leading-relaxed text-foreground/90 animate-fade-in"
            style={{ top: rateInfoPos.top, left: rateInfoPos.left }}
            data-testid="rate-info-popover"
          >
            {t('home.rate_info')}
          </div>,
          document.body
        )}

        <button
          onClick={toggleHide}
          className="balance-eye-btn p-1 transition-opacity opacity-70 hover:opacity-100"
          aria-label={t('home.toggle_balance', 'Показать или скрыть баланс')}
          data-testid="toggle-balance-visibility"
        >
          {hideBalance ? (
            <EyeOff className="w-5 h-5 text-muted-foreground" />
          ) : (
            <Eye className="w-5 h-5 text-muted-foreground" />
          )}
        </button>
      </div>

      {/* Изменение за сутки. Знак и стрелка обязательны: заметная доля людей
          не различает красный и зелёный, и цвет не может быть единственным
          носителем смысла. Показываем, только когда рынок ответил, — иначе
          на экране висел бы уверенный ноль вместо «данных пока нет». */}
      {change.known && (
        <div
          className={`balance-delta ${rising ? "is-up" : "is-down"}`}
          data-testid="balance-delta"
        >
          <TrendGlyph className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="tnum">{changeText}</span>
          <span className="balance-delta-dot" aria-hidden="true" />
          <span className="tnum">{percentText}</span>
          <span className="balance-delta-period">{t('home.delta_24h', '24ч')}</span>
        </div>
      )}
    </div>
  );
};

export default BalanceSection;
